import {
  BASE_FILTER_GROUP,
  BinaryColumnPredicateOp,
  CompoundColumnPredicateOp,
  FilledDynamicValueTableColumnType,
  Filter,
  FilterCellColumnPredicateOp,
  FilterCellListBinaryColumnPredicateOp,
  FilterGroup,
  FilterType,
  UnaryColumnPredicateOp,
} from "@hex/common";

import { HexSelectItem } from "../../../../hex-components";
import {
  FILTER_OP_TO_LABEL,
  FILTER_TYPE_TO_LABEL,
  GROUPED_FILTER_PREDICATE_OPS,
} from "../../shared/transform-cell/FilterDisplayValues";

function updateAt({
  group,
  position,
  updateGroup,
}: {
  group: FilterGroup;
  position: number[];
  updateGroup: (group: FilterGroup, index: number) => FilterGroup; // should not mutate its input
}): FilterGroup {
  const [index, ...rest] = position;
  if (rest.length === 0) {
    return updateGroup(group, index);
  } else {
    return {
      ...group,
      filters: group.filters.map((child, idx) => {
        if (idx === index) {
          if (FilterGroup.guard(child)) {
            return updateAt({
              group: child,
              position: rest,
              updateGroup,
            });
          } else {
            throw new Error("bad position");
          }
        } else {
          return child;
        }
      }),
    };
  }
}

function getFilterAt({
  filters,
  position,
}: {
  filters: FilterGroup;
  position: number[];
}): Filter | undefined {
  let currGroup: FilterGroup | Filter = filters;

  position.forEach((index) => {
    if (FilterGroup.guard(currGroup)) {
      currGroup = currGroup.filters[index];
    } else if (Filter.guard(currGroup)) {
      return currGroup;
    }
  });

  if (Filter.guard(currGroup)) {
    return currGroup;
  }
}

export function removeFilter({
  filters,
  position,
}: {
  filters: FilterGroup;
  position: number[];
}): FilterGroup {
  return updateAt({
    group: filters,
    position,
    updateGroup: (group, index) => ({
      ...group,
      filters: group.filters.filter((_, idx) => idx !== index),
    }),
  });
}

export function insertFilter({
  filterOrFilterGroupToInsert,
  filters,
  position,
}: {
  filterOrFilterGroupToInsert: Filter | FilterGroup;
  filters: FilterGroup | null;
  position: number[];
}): FilterGroup {
  if (filters == null || Object.keys(filters).length === 0) {
    // this is the first filter we're creating
    return {
      ...BASE_FILTER_GROUP,
      filters: [filterOrFilterGroupToInsert],
    };
  }
  return updateAt({
    group: filters,
    position,
    updateGroup: (group, index) => ({
      ...group,
      filters: [
        ...group.filters.slice(0, index),
        filterOrFilterGroupToInsert,
        ...group.filters.slice(index),
      ],
    }),
  });
}

export function moveFilter({
  currentPosition,
  destinationPosition,
  filters,
}: {
  filters: FilterGroup;
  currentPosition: number[];
  destinationPosition: number[];
}): FilterGroup | undefined {
  const filterToMove = getFilterAt({ filters, position: currentPosition });
  if (filterToMove == null) {
    return;
  }

  // if the destination position is after the current position, we need to delete then insert
  if (destinationPositionIsAfter(currentPosition, destinationPosition)) {
    const interimFilters = removeFilter({ filters, position: currentPosition });
    return insertFilter({
      filterOrFilterGroupToInsert: filterToMove,
      filters: interimFilters,
      position: destinationPosition,
    });
  }
  // if the destination position is before the current position, we need to insert then delete
  else {
    const interimFilters = insertFilter({
      filterOrFilterGroupToInsert: filterToMove,
      filters,
      position: destinationPosition,
    });
    return removeFilter({ filters: interimFilters, position: currentPosition });
  }
}

export function updateFilter({
  filters,
  newFilter,
  position,
}: {
  newFilter: Filter;
  filters: FilterGroup;
  position: number[];
}): FilterGroup {
  return updateAt({
    group: filters,
    position,
    updateGroup: (group, index) => ({
      ...group,
      filters: group.filters.map((filter, idx) => {
        if (idx === index) {
          return newFilter;
        } else {
          return filter;
        }
      }),
    }),
  });
}

export function updateFilterGroupOperation({
  filters,
  operation,
  position,
}: {
  operation: CompoundColumnPredicateOp;
  filters: FilterGroup;
  position: number[];
}): FilterGroup {
  if (position.length === 0) {
    return {
      ...filters,
      operation,
    };
  }

  return updateAt({
    group: filters,
    position,
    updateGroup: (group, index) => ({
      ...group,
      filters: group.filters.map((filter, idx) => {
        if (idx === index && FilterGroup.guard(filter)) {
          return { ...filter, operation };
        } else {
          return filter;
        }
      }),
    }),
  });
}

const GROUPED_FILTER_OPS: Record<string, FilterCellColumnPredicateOp[]> = {
  ...GROUPED_FILTER_PREDICATE_OPS,
  All: GROUPED_FILTER_PREDICATE_OPS["All"].filter(
    (op) => op !== UnaryColumnPredicateOp.ALWAYS,
  ),
};

export function getFilterOpsForColumnType(
  columnType: FilledDynamicValueTableColumnType,
  enableLumpFilterOps?: boolean,
): FilterCellColumnPredicateOp[] {
  switch (columnType) {
    case "STRING":
      return [
        ...GROUPED_FILTER_OPS["All"],
        ...GROUPED_FILTER_OPS["Text"],
        ...(enableLumpFilterOps ? GROUPED_FILTER_OPS["Lump"] : []),
      ];
    case "NUMBER":
      return [...GROUPED_FILTER_OPS["Number"], ...GROUPED_FILTER_OPS["All"]];
    case "DATE":
    case "DATETIME":
    case "DATETIMETZ":
      return [
        ...GROUPED_FILTER_OPS["Date"],
        ...GROUPED_FILTER_OPS["All"].filter(
          (op) =>
            // hide these in favor of the date-specific comparators
            // these are exact match which could be confusing
            op !== BinaryColumnPredicateOp.EQ &&
            op !== BinaryColumnPredicateOp.NEQ &&
            op !== FilterCellListBinaryColumnPredicateOp.IS_ONE_OF &&
            op !== FilterCellListBinaryColumnPredicateOp.NOT_ONE_OF,
        ),
      ];
    case "BOOLEAN":
      return [
        ...GROUPED_FILTER_OPS["Boolean"],
        ...GROUPED_FILTER_OPS["All"].filter(
          (op) =>
            // hide these in favor of the boolean specific comparators
            // these are exact match which could be confusing
            op !== BinaryColumnPredicateOp.EQ &&
            op !== BinaryColumnPredicateOp.NEQ &&
            op !== FilterCellListBinaryColumnPredicateOp.IS_ONE_OF &&
            op !== FilterCellListBinaryColumnPredicateOp.NOT_ONE_OF,
        ),
      ];
    case "TIME":
    case "UNKNOWN":
      return [...GROUPED_FILTER_OPS["All"]];
  }
}

export function filterOpToSelectItem(
  op: FilterCellColumnPredicateOp,
): HexSelectItem {
  return {
    title: FILTER_OP_TO_LABEL[op].toLowerCase(),
    value: op,
  };
}

export const filterTypeHexSelectItems = [
  {
    title: FILTER_TYPE_TO_LABEL[FilterType.KEEP],
    value: FilterType.KEEP,
  },
  {
    title: FILTER_TYPE_TO_LABEL[FilterType.REMOVE],
    value: FilterType.REMOVE,
  },
];

export function humanReadableFilterType(filterType: FilterType): string {
  return FILTER_TYPE_TO_LABEL[filterType];
}

export function distinctValueToSelectItem(
  value: string | number | boolean,
): HexSelectItem {
  return {
    title: value?.toString(),
    value: value?.toString(),
  };
}

export function notNullOrEmptyString(
  value: string | number | null | boolean,
): value is string | number | boolean {
  return value != null && value !== "";
}

export function getSelectItemByValue({
  items,
  value,
}: {
  items: HexSelectItem[];
  value: string;
}): HexSelectItem | null {
  return items.filter((item) => item.value === value)[0] ?? null;
}

function destinationPositionIsAfter(
  currentPosition: number[],
  destinationPosition: number[],
): boolean {
  for (
    let i = 0;
    i < Math.min(currentPosition.length, destinationPosition.length);
    i++
  ) {
    if (currentPosition[i] < destinationPosition[i]) {
      return false;
    } else if (currentPosition[i] > destinationPosition[i]) {
      return true;
    }
  }
  return true;
}

//// Shared style constants
export const FilterGroupSpacing = 14;
export const FilterColumnWidth = 210;
export const FilterValueWidth = 250;

export const getFilterWidthCss = (
  width: string | number | undefined,
): string => {
  if (width == null) {
    return `width: ${FilterValueWidth}px;`;
  }
  const resolvedWidth = typeof width === "string" ? width : `${width}px`;
  return `width: ${resolvedWidth};`;
};
